/* eslint-disable react-hooks/exhaustive-deps */
/* React */
import React, { useEffect, useState, useMemo } from 'react';

/* Routes */
import { useRouter } from 'routes';

/* Translation */
import { useTranslation } from 'react-i18next';

/* Components */
import Breadcrumbs from 'components/shared/Breadcrumbs';
import CenterInfo from './CenterInfo';
import CenterGeneral from './CenterGeneral';
import CenterConfig from './CenterConfig';
import TeacherTab from './TeacherTab';
import DirectorsTab from './DirectorsTab';

/* Styled Components */
import {
  StyledCenterView,
  Row,
  Container,
  Header,
  Body,
  StyledOptioner,
} from './styles/CenterView';
import { Dropdown } from 'lego-dist';
import {
  ItemDropdown,
  Overlay,
  Dots,
  ExportDataOption,
} from '../organization/styles/OrganizationView';

/* Themes */
import { SliderBar } from 'themes/lego/Optioner/Optioner.theme';

/* Services, Hooks, Constants */
import useCenters from 'hooks/centers/UseCenters';
import useCurrentUser from 'hooks/useCurrentUser';
import useOrganization from 'hooks/UseOrganization';
import { APP_SECTIONS_PATHS } from 'constants/routes';
import GroupService from '../../../services/GroupService';
import { setLocalStorageItem } from 'lib/helpers';
import { setCoreAuthToken } from 'api/CoreInstance';
import { KEYS_LOCALSTORAGE } from 'constants/general';
import { useCookies } from 'react-cookie';
import EventReporter from 'appEvents';
import { EventProviders, AppEvents } from 'constants/events';
import ExportDataModalView from '../organization/modals/ExportDataModalView';

const setCalendarApi = (center: any) => {
  if (window.KineduCalendarApi && center.calendar_group_id) {
    window.KineduCalendarApi.setCurrentCenterId(center.calendar_group_id);
  }
};

/* tabs logic */
const sections: any = {
  teachers: TeacherTab,
  config: CenterConfig,
  general: CenterGeneral,
  directors: DirectorsTab,
};

interface CenterViewProps { }

export enum IETabValues {
  GENERAL = 'general',
  DIRECTORS = 'directors',
  TEACHERS = 'teachers',
}
const groupService = GroupService.getInstance();

const CenterView: React.FC<CenterViewProps> = () => {
  const { currentParamsRef } = useRouter();
  const { t } = useTranslation();
  const [classrooms, setClassrooms] = useState<any[]>();
  const [coreClassrooms, setCoreClassrooms] = useState<any[]>();
  const [centerConfiguration, setCenterConfiguration] = useState<any>();
  const { user, isTeacher, isOwnerOrAdmin } = useCurrentUser();

  const [teachers, setTeachers] = useState<any[]>();
  const [teacherGroups, setTeacherGroups] = useState<any>([]);
  // used for the export data modal
  const { organization } = useOrganization();
  const [openExportData, setOpenExportData] = useState(false);
  const [cookies] = useCookies(['kinedu-export-data-cookie']);

  const { fetchCenterById, center, setActiveCenterById, fetchCenters } = useCenters();

  const {
    current: { centerId, tab },
  } = currentParamsRef;

  const fetchGroupByCenter = async (centerId: number) => {
    const coreGroups: any = await groupService.getGroupsByCenterCore(centerId);
    setCenterConfiguration(coreGroups?.center?.center_configuration || {});
    setCoreClassrooms(coreGroups?.center?.groups);
  };

  const refreshData = () => {
    fetchCenterById(centerId);
    fetchCenters();
  }

  useEffect(() => {
    fetchCenterById(centerId);
  }, [centerId]);


  useEffect(() => {
    if (center !== undefined) {
      setClassrooms(center?.groups);
      fetchGroupByCenter(center?.external_id);
      setTeachers(center?.teachers);

      if (center?.id) {
        setCoreAuthToken(center?.id);
        setLocalStorageItem(KEYS_LOCALSTORAGE.ACTIVE_CENTER_TOKEN, center?.id);
        groupService.setGroupList$([]);
        setActiveCenterById(center?.id, center);
        setCalendarApi(center);
      }
    }
  }, [center]);

  /* set tabs */
  const [selectedTab, setSelectedTab] = useState(tab || IETabValues.GENERAL);
  const tabs = [
    { section: IETabValues.GENERAL, label: t('settings:GENERAL'), value: IETabValues.GENERAL },
    {
      section: IETabValues.DIRECTORS,
      label: t('settings:DIRECTORS'),
      value: IETabValues.DIRECTORS,
    },
    { section: IETabValues.TEACHERS, label: t('settings:TEACHERS'), value: IETabValues.TEACHERS },
  ];

  // NOTE: This is temporarily hidden until we solve the issue with exotic configurations.
  // if (can(ActionPermission.EDIT_CENTER_CONFIGURATION)) {
  //   tabs.push({ section: 'config', label: t('settings:CONFIG'), value: 'config' });
  // }

  const CurrentSection = sections[selectedTab];

  const handleOnFinishAddTeacherFlow = () => fetchCenterById(centerId);

  useMemo(() => {
    if (isTeacher && classrooms?.length) {
      setTeacherGroups(classrooms?.filter((c: any) => user?.groups?.map((g: any) => g?.id).includes(c?.id)));
    }
  }, [classrooms]);

  const registerExportDataAction = () => {
    const params = {
      source: 'threeDotMenuCenter',
      user_role: user.type,
    };
    EventReporter.action(AppEvents.EXPORT_DATA(params), [EventProviders.MIXPANEL]);
  };

  const isDisabled = useMemo(() => 'kinedu-export-data-cookie' in cookies, [cookies]);
  const handleExportData = (close: Function) => {
    if (isDisabled) {
      return;
    }
    registerExportDataAction();
    setOpenExportData(true);
    close();
  };

  const isKineduOnline = useMemo(() => {
    return organization?.name.includes('Kinedu Online');
  }, [organization?.name]);

  const showMenuForED = !isKineduOnline && isOwnerOrAdmin;
  const EDOption = ({ close }: any) => (
    <ExportDataOption onClick={() => handleExportData(close)} aria-hidden="true" isDisabled={isDisabled}>
      {t('settings:EXPORT_DATA.MULTIPLE_ACTION')}
    </ExportDataOption>
  );

  return (
    <>
      <StyledCenterView>
        <Row>

          <Breadcrumbs
            links={[
              {
                name: t('settings:SETTINGS'),
                url: APP_SECTIONS_PATHS.SETTINGS,
              },
            ]}
            color="#32AFE0"
          />

          <Container>
            <Header>
              {t('settings:CENTER_SECTION.CENTER_INFORMATION')}
              {
                showMenuForED && (
                  <Dropdown
                    className="DropdownRemove"
                    contentWidth={100}
                    noPaddingContent
                    content={({ close }: any) => (
                      <>
                        <Overlay onClick={() => close()} />
                        <ItemDropdown>
                          <EDOption close={close} />
                        </ItemDropdown>
                      </>
                    )}
                    showIndicator={false}
                    title={() => <Dots />}
                  />

                )
              }
            </Header>
            <Body>
              {center && <CenterInfo center={center} />}
              <StyledOptioner
                theme={SliderBar}
                options={tabs}
                value={selectedTab}
                returnValue="object"
                onChange={(value: any) => setSelectedTab(value.section)}
              />
              {CurrentSection && center && classrooms && (
                <CurrentSection
                  center={center}
                  directors={center?.directors}
                  refreshData={refreshData}
                  classrooms={isTeacher ? teacherGroups || [] : classrooms}
                  coreClassrooms={coreClassrooms}
                  teachers={teachers}
                  configuration={centerConfiguration}
                  handleOnFinishAddTeacherFlow={handleOnFinishAddTeacherFlow}
                />
              )}
            </Body>
          </Container>
        </Row>
      </StyledCenterView>
      {
        center && (
          <ExportDataModalView
            selectedCenter={center.id}
            openModal={openExportData}
            onCloseModal={() => setOpenExportData(false)}
          />
        )
      }
    </>
  );
};

export default CenterView;
